// Turnaround / Need-By date helpers. The live consumer is the portal's Need-By
// picker (NeedByPicker), which uses `defaultNeedByDate` + `addCalendarDays` +
// `STANDARD_TURNAROUND_DAYS` to offer the lanes and phrase the window.
//
// `isExpeditedNeedBy` is NOT how a quote becomes expedited, and nothing outside
// this file's own tests calls it. Expedite is the customer's explicit
// `Quote.expedite_requested` answer, persisted at save and read straight off the
// row by the pricing builder (services/pricing/quote-services.ts). Deriving it
// from the Need-By date at read time is the defect R26 names: the comparison
// moves as the clock does, so a plain Standard quote drifted into "expedited"
// simply by sitting there. The helper stays as the pure date predicate a
// SUGGESTION could be built on; it must not be wired to the transmitted intent.
//
// The money (the uplift) is computed and owned by Nexus either way — see
// vocabulary.ts STANDARD_TURNAROUND_DAYS / EXPEDITE_FAMILY_KEY.
//
// All dates here are calendar-day strings in `YYYY-MM-DD` form (the shape an
// <input type="date"> emits and the shape Quote.requested_finish_date
// serializes to). Comparisons are lexicographic, which is chronological for
// zero-padded ISO dates. Callers pass their own "today" so these stay pure
// and testable (and so the portal can use local-time today while the backend
// uses its own).
import { STANDARD_TURNAROUND_DAYS, TURNAROUND_DAYS_BY_PRODUCT_TYPE } from '../vocabulary.js';
// Add (or subtract) whole calendar days to a `YYYY-MM-DD` date, returning a
// `YYYY-MM-DD` date. Pure calendar arithmetic via UTC — no local-timezone
// drift, and month/year rollover is handled by the Date math.
export function addCalendarDays(isoDate, days) {
    const [y, m, d] = isoDate.split('-').map((p) => Number.parseInt(p, 10));
    const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
    dt.setUTCDate(dt.getUTCDate() + days);
    return dt.toISOString().slice(0, 10);
}
// The standard production turnaround (calendar days) for a single product type.
// Falls back to STANDARD_TURNAROUND_DAYS for any type without an override (see
// TURNAROUND_DAYS_BY_PRODUCT_TYPE in vocabulary.ts). Accepts a loose string so
// callers can pass a raw config.product_type without narrowing first.
export function turnaroundDaysForProductType(productType) {
    // `Object.hasOwn` before the read: the parameter is a loose string (callers
    // pass a raw config.product_type), and the map is a plain object literal, so
    // 'constructor' would resolve to `Object` itself — not nullish, so `??` never
    // fires and a function escapes a `number` return.
    const days = productType && Object.hasOwn(TURNAROUND_DAYS_BY_PRODUCT_TYPE, productType)
        ? TURNAROUND_DAYS_BY_PRODUCT_TYPE[productType]
        : undefined;
    return days ?? STANDARD_TURNAROUND_DAYS;
}
// The standard turnaround for a whole quote/order: the LONGEST among its lines'
// product types — the order isn't finished until its slowest piece is (e.g. a
// hard-bound book stretches the whole order's default Need-By). An empty list
// (or all-standard) yields STANDARD_TURNAROUND_DAYS.
export function turnaroundDaysForProductTypes(productTypes) {
    let max = STANDARD_TURNAROUND_DAYS;
    for (const pt of productTypes) {
        const d = turnaroundDaysForProductType(pt);
        if (d > max)
            max = d;
    }
    return max;
}
// The default Need-By date for a new quote: today + the turnaround window.
// `todayIso` is the caller's notion of today as `YYYY-MM-DD`. `turnaroundDays`
// defaults to STANDARD_TURNAROUND_DAYS; pass a family-aware value (from
// turnaroundDaysForProductTypes) so a hard-bound order defaults to its longer
// window.
export function defaultNeedByDate(todayIso, turnaroundDays = STANDARD_TURNAROUND_DAYS) {
    return addCalendarDays(todayIso, turnaroundDays);
}
// Is a chosen Need-By date expedited? True iff it falls strictly before the
// standard-turnaround default (today + `turnaroundDays`) — i.e. the customer
// wants it in hand sooner than the normal window for THIS order's products. A
// null / empty Need-By is never expedited (no rush requested). Pass the same
// family-aware `turnaroundDays` used for the default so the rush threshold and
// the default stay in agreement.
export function isExpeditedNeedBy(needByIso, todayIso, turnaroundDays = STANDARD_TURNAROUND_DAYS) {
    if (!needByIso)
        return false;
    return needByIso < defaultNeedByDate(todayIso, turnaroundDays);
}
//# sourceMappingURL=turnaround.js.map